import React, { SetStateAction, useState, useEffect, Suspense } from 'react';

import {
  Typography,
  Button,
  Box,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  CircularProgress,
  Switch,
  TextField,
} from '@mui/material';

import { IUserDocumentTemplate } from '@homeporter/types';
import { IProfile, IUserSettings } from '../../../global/interfaces';
import { StyledModal, useFormStyles } from './styles';
import { useCopilotContext } from '../../../contexts/CopilotContext';
import SaveIcon from '../../../icons/SaveIcon';
import FilePreviewSelector from '../../../components/FilePreviewSelector';
import { axiosRequest } from '../../../utils/axiosRequest';
import toggleToast from '../../../utils/messaging';
import { FormEditor } from './FormEditor';
import FormView from '../../../components/KnowledgeLibrary/FormView';

const uploadData = async (
  file: File | undefined,
  url: string,
  setUpdateStatus: (_: string) => void,
) => {
  const axios = axiosRequest();
  const formData = new FormData();
  if (file) formData.append('file', file);
  formData.append('url', url);

  console.log('::::upload file:', file);

  const response = await axios.post('/bookings/upload-attachment', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent: any) => {
      const percentCompleted = Math.round(
        (progressEvent.loaded * 100) / progressEvent.total,
      );
      setUpdateStatus(
        `${file ? 'Uploading' : 'Deleting'} attachment ${url.split('/').pop() || ''} (${percentCompleted}%)`,
      );
    },
  });

  return response;
};

interface IProps {
  profile: IProfile | undefined;
  userSettings: IUserSettings | undefined;
  documentTemplates: IUserDocumentTemplate[];
  isModalOpen: boolean;
  handleCloseModal: React.Dispatch<SetStateAction<boolean>>;
}

const FormLibrary = ({
  profile,
  userSettings,
  documentTemplates,
  isModalOpen,
  handleCloseModal,
}: IProps) => {
  const styles = useFormStyles();
  const {
    formList,
    refetchDocuments,
    documentLoading,
    updateDocument,
    viewDocument: selectedForm,
    setViewDocument: setSelectedForm,
  } = useCopilotContext();

  const [selectedDefaultTemplate, setSelectedDefaultTemplate] =
    useState<IUserDocumentTemplate | null>(null);

  const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);
  const [showRawData, setShowRawData] = useState<boolean>(false);
  const [saveEnabled, setSaveEnabled] = useState<boolean>(false);
  const [imageFile, setImageFile] = useState<File | undefined>();
  const [sourceFile, setSourceFile] = useState<File | undefined>();

  const [isSaving, setIsSaving] = useState<string>('');
  const [formTitle, setFormTitle] = useState<string>('');
  const [formCode, setFormCode] = useState<string>('');
  const [formDataStr, setFormDataStr] = useState<string>('');
  const [originalFormData, setOriginalFormData] = useState<string>('');
  const [organizationId, setOrganizationId] = useState<number>(0);

  const [isValidFormData, setIsValidFormData] = useState<boolean>(true);

  useEffect(() => {
    try {
      JSON.parse(formDataStr);
      setIsValidFormData(true);
    } catch (err) {
      setIsValidFormData(false);
    }
  }, [formDataStr]);

  useEffect(() => {
    if (!documentLoading && selectedForm) {
      const timeout = setTimeout(() => {
        const _originalFormData = JSON.stringify(
          selectedForm?.data || '',
          null,
          2,
        );
        setFormDataStr(_originalFormData);
        setOriginalFormData(_originalFormData);
        /* Force fetching the data each time the form is selected
        const selected = formList.find((t) => t.did === selectedForm?.did);
        if (selected && selectedForm?.data) {
          selected.data = selectedForm.data;
          console.log('::::selected', selected);
        }
        */
      }, 0); // delay to next event loop

      return () => clearTimeout(timeout);
    } else {
      setFormDataStr('');
    }
  }, [documentLoading]);

  useEffect(() => {
    console.log('::::useEffect selectedForm: ', selectedForm);

    if (selectedForm) {
      setFormTitle(selectedForm.title);
      setFormCode(selectedForm.code || '');
      setOrganizationId(selectedForm.organization_id || 0);
      setSelectedDefaultTemplate(
        selectedForm.default_template_id
          ? (documentTemplates.find(
              (t) => t.dtid === selectedForm.default_template_id,
            ) ?? null)
          : null,
      );
      setFormDataStr(
        selectedForm.data ? JSON.stringify(selectedForm.data, null, 2) : '',
      );
    } else {
      setFormTitle('');
      setFormCode('');
      setFormDataStr('');
      setImageFile(undefined);
      setSourceFile(undefined);
      setSelectedDefaultTemplate(null);
      setOrganizationId(0);
    }
  }, [selectedForm]);

  const saveForm = async () => {
    console.log('::::saveForm');
    if (!selectedForm) return;

    try {
      setIsSaving('Saving...');

      const form = { ...selectedForm };
      form.title = formTitle;
      form.code = formCode;
      form.default_template_id = selectedDefaultTemplate?.dtid || 0;
      form.organization_id = organizationId;
      form.original_organization_id = selectedForm.organization_id || 0;
      form.data = JSON.parse(formDataStr);

      if (imageFile) form.images = [imageFile.name];
      if (sourceFile) form.source = sourceFile.name;

      const result = await updateDocument(form);
      console.log('::::result', result);
      if (result.status !== 'success' || !result.document) {
        throw new Error('Error saving form');
      }

      let response;
      if (imageFile) {
        if (selectedForm?.images?.[0]) {
          response = await uploadData(
            undefined,
            selectedForm?.images?.[0],
            setIsSaving,
          );
          console.log('::::response:', response.data);
        }

        response = await uploadData(
          imageFile,
          result.document.images[0],
          setIsSaving,
        );
        console.log('::::response:', response.data);
      }

      if (sourceFile) {
        if (selectedForm?.source) {
          response = await uploadData(
            undefined,
            selectedForm?.source,
            setIsSaving,
          );
          console.log('::::response:', response.data);
        }
        response = await uploadData(
          sourceFile,
          result.document.source,
          setIsSaving,
        );
        console.log('::::response:', response.data);
      }

      refetchDocuments();
      setImageFile(undefined);
      setSourceFile(undefined);
      setFormDataStr('');
      setSelectedForm(result.document);
      setIsSaving('');
    } catch (error: any) {
      toggleToast('Error saving form', 'error');
      setIsSaving('');
    }
  };

  const newForm = () => {
    setFormDataStr('[]');
    setOriginalFormData('[]');

    setSelectedForm({
      did: -1,
      user_id: profile?.user_id ?? 0,
      title: 'New Form',
      code: 'NF',
      default_template_id: 0,
      key: '',
      type: 'FORM',
      source: '',
      images: [],
      data: [],
    });
  };

  useEffect(() => {
    if (!selectedForm) return setSaveEnabled(false);

    const _enableSave =
      (selectedForm.title !== formTitle.trim() ||
        selectedForm.code !== formCode.trim() ||
        (selectedForm.organization_id || 0) !== organizationId ||
        selectedForm.default_template_id !==
          (selectedDefaultTemplate?.dtid ?? 0) ||
        (isValidFormData && originalFormData !== formDataStr) ||
        !!imageFile?.name ||
        !!sourceFile?.name) &&
      formTitle.trim() !== '' &&
      formCode.trim() !== '' &&
      (selectedForm.did > 0 || (!!imageFile?.name && !!sourceFile?.name));

    setSaveEnabled(_enableSave);
  }, [
    selectedForm,
    formTitle,
    formCode,
    sourceFile,
    imageFile,
    selectedDefaultTemplate,
    documentLoading,
    organizationId,
    formDataStr,
  ]);

  useEffect(() => {
    console.log('::::FormList useEffect formList', formList);
  }, [formList]);

  return (
    <>
      <Modal open={isModalOpen} onClose={() => handleCloseModal(false)}>
        <StyledModal
          id="form-library-modal"
          sx={{ height: 'min(85rem, calc(100vh - 5rem))' }}>
          {selectedForm?.did !== -1 && (
            <FormControl fullWidth sx={{ mt: '1rem' }}>
              <InputLabel sx={styles.inputLabel}>Select Form</InputLabel>
              <Select
                value={selectedForm?.did || ''}
                onChange={(e) => {
                  const selected = formList.find(
                    (t) => t.did === e.target.value,
                  );
                  setSelectedForm(
                    JSON.parse(JSON.stringify(selected ?? '')) ?? null,
                  );
                }}
                sx={styles.select}
                variant="outlined">
                {formList.map((form) => (
                  <MenuItem key={form.did} value={form.did}>
                    {`${form.code ? `[${form.code}] ` : ''}${form.title}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {selectedForm && (
            <>
              <FormControl fullWidth sx={{ mt: '1.5rem' }}>
                <InputLabel sx={styles.inputLabel}>
                  Document template to apply when work object is added to new
                  appointment
                </InputLabel>
                <Select
                  value={selectedDefaultTemplate?.dtid || ''}
                  disabled={!selectedForm}
                  onChange={(e) => {
                    setSelectedDefaultTemplate(
                      documentTemplates.find(
                        (t) => t.dtid === e.target.value,
                      ) ?? null,
                    );
                  }}
                  sx={styles.select}
                  variant="outlined">
                  <MenuItem value={0}>None</MenuItem>
                  {documentTemplates.map((template) => (
                    <MenuItem key={template.dtid} value={template.dtid}>
                      {template.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ mt: '2rem' }}>
                <TextField
                  label="Form Title"
                  value={formTitle}
                  disabled={!selectedForm}
                  onChange={(e) => {
                    setFormTitle(e.target.value);
                  }}
                  sx={{ width: '70%' }}
                />

                <TextField
                  label="Form Code"
                  value={formCode}
                  disabled={!selectedForm}
                  onChange={(e) => {
                    setFormCode(e.target.value);
                  }}
                  sx={{ ml: '2rem', width: 'calc(30% - 2rem)' }}
                />
              </Box>

              <FilePreviewSelector
                label="Source Document"
                type=".xlsx,.docx,.pdf"
                selectedFile={sourceFile}
                setSelectedFile={setSourceFile}
                url={selectedForm?.source || ''}
              />

              <FilePreviewSelector
                label="Image Preview"
                type=".webp,.png,.jpg,.jpeg"
                selectedFile={imageFile}
                setSelectedFile={setImageFile}
                url={selectedForm.images?.[0] || ''}
              />

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mt: '1rem',
                  mb: '.5rem',
                }}>
                <Typography variant="subtitle2" sx={{ mr: 2 }}>
                  Form Data:
                </Typography>
                {userSettings?.dev && (
                  <>
                    <Typography variant="subtitle2" sx={{ mr: 2 }}>
                      Show Raw Data:
                    </Typography>
                    <Switch
                      checked={showRawData}
                      onChange={(e) => setShowRawData(e.target.checked)}
                    />
                  </>
                )}
                {isValidFormData && (
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ ml: 'auto' }}
                    onClick={() => setShowPreviewModal(true)}>
                    Preview Form
                  </Button>
                )}
                {!isValidFormData && (
                  <Typography variant="subtitle2" sx={styles.invalidFormData}>
                    Invalid Form Data
                  </Typography>
                )}
              </Box>

              {showRawData && (
                <Box sx={styles.formData}>
                  {documentLoading && (
                    <Box sx={styles.documentLoading}>
                      <CircularProgress size={'2rem'} sx={{ mr: '.5rem' }} />
                    </Box>
                  )}
                  {!documentLoading && ( //ideally hide idx and blank fields
                    <TextField
                      multiline
                      fullWidth
                      value={formDataStr}
                      onChange={(e) => {
                        setFormDataStr(e.target.value);
                      }}
                      sx={styles.pre}
                    />
                  )}
                </Box>
              )}
              {!showRawData && (
                <FormEditor
                  formDataStr={formDataStr}
                  setFormDataStr={setFormDataStr}
                  documentLoading={documentLoading}
                />
              )}
            </>
          )}

          <Box sx={styles.buttonContainer}>
            {(selectedForm?.did || -1) > 0 && (
              <Box sx={styles.shareWithOrganization}>
                <Typography sx={{ mr: 2 }}>Share with Organization</Typography>
                <Switch
                  checked={organizationId === profile?.organization_id}
                  onChange={(e) => {
                    if (selectedForm) {
                      setOrganizationId(
                        e.target.checked ? (profile?.organization_id ?? 0) : 0,
                      );
                    }
                  }}
                />
              </Box>
            )}
            <>
              <Button
                variant="outlined"
                disabled={isSaving !== ''}
                onClick={() => {
                  if (selectedForm?.did === -1) {
                    setSelectedForm(null);
                  } else if (!saveEnabled) {
                    newForm();
                  } else {
                    const selected = formList.find(
                      (t) => t.did === selectedForm?.did,
                    );
                    setSelectedForm(
                      JSON.parse(JSON.stringify(selected ?? '')) ?? null,
                    );
                  }
                }}
                sx={{
                  ml: 'auto',
                }}>
                {selectedForm?.did === -1
                  ? 'Drop New Form'
                  : saveEnabled
                    ? 'Drop Changes'
                    : 'Add New Form'}
              </Button>

              <Button
                variant="contained"
                disabled={!saveEnabled || isSaving !== ''}
                onClick={() => {
                  saveForm();
                }}
                startIcon={
                  isSaving ? (
                    <CircularProgress size={'1.25rem'} sx={{ mr: '.5rem' }} />
                  ) : (
                    <SaveIcon sx={{ mr: '.5rem' }} />
                  )
                }>
                {isSaving ? isSaving : 'Save Form'}
              </Button>
            </>
          </Box>
        </StyledModal>
      </Modal>

      <Modal open={showPreviewModal} onClose={() => setShowPreviewModal(false)}>
        <StyledModal sx={styles.previewModal}>
          <Box
            sx={{ m: '1rem', overflow: 'auto', height: 'calc(100% - 4.5rem)' }}>
            <FormView data={JSON.parse(formDataStr || '[]')} />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="text" onClick={() => setShowPreviewModal(false)}>
              Close
            </Button>
          </Box>
        </StyledModal>
      </Modal>
    </>
  );
};

export default FormLibrary;
